import React, { useState, useEffect } from 'react'
import { IconButton, Popover, Box, Typography, Tooltip, Link, Divider, Button } from '@mui/material'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import FolderOutlinedIcon from '@mui/icons-material/FolderOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import APIService from 'services/APIService'

const SYNC_MODE_LABELS = {
  read_write: 'Read & write — the Building Passport always mirrors the folder structure and files in SharePoint; two-way editing back to SharePoint is coming soon',
  read_only: 'Read only — SharePoint updates come in, nothing is sent back',
  one_time: 'One-time import — synced once, not kept in step',
}

const STATUS_LABELS = {
  idle: 'Up to date',
  completed: 'Up to date',
  running: 'Syncing now…',
  pending: 'Waiting to sync',
  error: 'Last sync had a problem',
  failed: 'Last sync had a problem',
}

const formatDateTime = value => {
  if (!value) return null
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return null
  return d.toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  })
}

const SharePointViewInfo = ({ view, bpId, syncStatus, onChangeFolder }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [details, setDetails] = useState(null)
  const open = Boolean(anchorEl)

  useEffect(() => {
    if (!open || !bpId || !view?.id) return
    let cancelled = false
    const api = APIService()
    api.get(`/building-passport/${bpId}/custom-views/${view.id}/sharepoint/status`)
      .then(res => {
        if (cancelled) return
        setDetails(res?.data?.data || null)
      })
      .catch(() => { if (!cancelled) setDetails(null) })
    return () => { cancelled = true }
  }, [open, bpId, view?.id])

  useEffect(() => {
    if (!open) return undefined
    const close = () => setAnchorEl(null)
    window.addEventListener('scroll', close, true)
    return () => window.removeEventListener('scroll', close, true)
  }, [open])

  if (!view || view.integrationType !== 'sharepoint') return null

  const folder = view.sharepointFolderPath || 'Entire library (root folder)'
  const syncMode = SYNC_MODE_LABELS[view.sharepointSyncMode] || 'Read & write — the Building Passport always mirrors the folder structure and files in SharePoint; two-way editing back to SharePoint is coming soon'
  const statusKey = (details?.syncStatus?.status || syncStatus?.status || 'idle')
  const statusLabel = STATUS_LABELS[statusKey] || statusKey
  const lastSynced = formatDateTime(details?.lastSyncAt ?? view.lastSyncAt)
  const webUrl = view.sharepointWebUrl || null

  return (
    <>
      <Tooltip title='About this SharePoint view' placement='top'>
        <IconButton
          size='small'
          aria-label='About this SharePoint view'
          onClick={e => setAnchorEl(e.currentTarget)}
          sx={{ p: 0.5, color: '#4d4d56', '&:hover': { backgroundColor: 'action.hover', color: '#12132e' } }}
        >
          <InfoOutlinedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Tooltip>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        sx={{ zIndex: 20005 }}
        slotProps={{ paper: { sx: { width: 320, maxWidth: 'calc(100vw - 32px)', borderRadius: 2, p: 2, boxShadow: '0 12px 32px rgba(12,18,41,0.18)' } } }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 800, color: '#12132e', mb: 1.5 }}>
          SharePoint sync details
        </Typography>

        <InfoRow label='Synced folder' icon={<FolderOutlinedIcon sx={{ fontSize: 15, color: '#9a9aa5' }} />}>
          {folder}
        </InfoRow>
        {!view.sharepointFolderPath && (
          <Typography sx={{ fontSize: 12.5, lineHeight: 1.4, color: '#c2410c', backgroundColor: '#ffedd5', borderRadius: 1, px: 1, py: 0.75, mb: 1.1 }}>
            This view copies every folder in the library, which may include files for other buildings.
          </Typography>
        )}
        {onChangeFolder && (
          <Button
            variant='outlined'
            size='small'
            startIcon={<FolderOutlinedIcon />}
            onClick={() => { setAnchorEl(null); onChangeFolder() }}
            sx={{ mb: 1.5, minHeight: 40, textTransform: 'none', fontWeight: 700, fontSize: 13.5, color: '#2aa6b3', borderColor: '#33becc', '&:hover': { borderColor: '#2aa6b3', backgroundColor: '#e6f7f9' } }}
          >
            Change synced folder
          </Button>
        )}
        <InfoRow label='Sync mode'>{syncMode}</InfoRow>
        <InfoRow label='Status'>{statusLabel}</InfoRow>
        <InfoRow label='Last synced'>{lastSynced || 'Not synced yet'}</InfoRow>

        {webUrl && (
          <>
            <Divider sx={{ my: 1.25 }} />
            <Link
              href={webUrl}
              target='_blank'
              rel='noopener noreferrer'
              sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, fontSize: 12.5, fontWeight: 700 }}
            >
              Open folder in SharePoint
              <OpenInNewIcon sx={{ fontSize: 14 }} />
            </Link>
          </>
        )}
      </Popover>
    </>
  )
}

const InfoRow = ({ label, icon, children }) => (
  <Box sx={{ mb: 1.1 }}>
    <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#8a8a95', mb: 0.25 }}>
      {label}
    </Typography>
    <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.5, fontSize: 13, lineHeight: 1.4, color: '#2a2a35', wordBreak: 'break-word' }}>
      {icon}
      <span>{children}</span>
    </Box>
  </Box>
)

export default SharePointViewInfo
